import React from 'react';
import { useForm } from '../context/FormContext';
import { YES_NO_QUESTIONS } from './yesNoConfig';
import { formatMonthYear, formatDate } from '../utils/validation';
import { formatDayLabel, totalHours } from '../utils/dotHours';

export default function StepReview({ onNext, onBack, onGoToStep }) {
  const { state } = useForm();
  const p = state.personal;
  const addr = state.currentAddress;
  const prev = state.previousAddress;
  const c = state.contact;
  const lic = state.license;
  const sig = state.signature;
  const hasPrev = parseFloat(addr.years) < 3;

  const Section = ({ title, stepId, children }) => (
    <div className="review-section">
      <div className="review-section-header">
        <div className="review-section-title">{title}</div>
        <button className="btn-edit" onClick={() => onGoToStep(stepId)}>Edit</button>
      </div>
      <div className="step-card" style={{ marginBottom: 0, paddingTop: 16, paddingBottom: 16 }}>
        {children}
      </div>
    </div>
  );

  const Row = ({ label, value }) => (
    <div className="review-row"><span className="review-label">{label}: </span>{value || <em style={{ color: 'var(--text-light)' }}>Not provided</em>}</div>
  );

  const ssnMasked = state.ssn ? `XXX-XX-${state.ssn.slice(-4)}` : '—';

  return (
    <div>
      <div className="step-card">
        <h2 className="step-title">Review Your Application</h2>
        <p className="step-subtitle">Tap "Edit" on any section to make changes. You'll return here when done.</p>
      </div>

      <Section title="Company of Application" stepId="company">
        <Row label="Company" value={state.companyOfApplication} />
      </Section>

      <Section title="Personal Information" stepId="personal">
        <Row label="Name" value={[p.firstName, p.middleName, p.lastName, p.suffix].filter(Boolean).join(' ')} />
      </Section>

      <Section title="Date of Birth" stepId="dob">
        <Row label="DOB" value={state.dateOfBirth} />
      </Section>

      <Section title="Social Security Number" stepId="ssn">
        <Row label="SSN" value={ssnMasked} />
      </Section>

      <Section title="Address" stepId="address">
        <Row label="Current" value={`${addr.street}, ${addr.city}, ${addr.state} ${addr.zip} — ${addr.years} years`} />
        {hasPrev && prev.city && <Row label="Previous" value={`${prev.street}, ${prev.city}, ${prev.state} ${prev.zip}`} />}
      </Section>

      <Section title="Contact Information" stepId="contact">
        <Row label="Home" value={c.homePhone || 'N/A'} />
        <Row label="Cell" value={c.cellPhone || 'N/A'} />
        <Row label="Email" value={c.email || 'N/A'} />
      </Section>

      <Section title="Driver's License" stepId="license">
        <Row label="License #" value={lic.number} />
        <Row label="State" value={lic.state} />
        <Row label="Class" value={lic.class} />
        <Row label="Issued" value={formatDate(lic.issueDate)} />
        <Row label="Expires" value={formatDate(lic.expirationDate)} />
        <Row label="Years Held" value={lic.yearsHeld} />
        <Row label="Endorsements" value={lic.endorsements} />
        <Row label="Restrictions" value={lic.restrictions} />
        <Row label="States (5yr)" value={lic.lower48 ? 'Lower 48' : lic.statesOperated.join(', ')} />
        <Row label="License Photo" value={lic.photoBase64 ? '✅ Uploaded' : '⚠️ Not uploaded'} />
      </Section>

      <Section title="Background Questions" stepId="q_0">
        {YES_NO_QUESTIONS.map((q, i) => {
          const yn = state.yesNo[q.key];
          if (q.conditionalOn) {
            const { key, value } = q.conditionalOn;
            if (state.yesNo[key]?.answer !== value) return null;
          }
          return (
            <div key={q.key} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 13, color: 'var(--text-light)', lineHeight: 1.4 }}>{q.question}</div>
              <div style={{ fontWeight: 700, color: yn.answer === 'yes' ? 'var(--error)' : 'var(--success)' }}>
                {yn.answer ? yn.answer.toUpperCase() : '—'}
              </div>
              {yn.explanation && <div style={{ fontSize: 13, fontStyle: 'italic', color: 'var(--text)', marginTop: 2 }}>{yn.explanation}</div>}
              {yn.previousState && <div style={{ fontSize: 13, color: 'var(--text)', marginTop: 2 }}>State: {yn.previousState} | License: {yn.previousLicenseNumber}</div>}
            </div>
          );
        })}
      </Section>

      <Section title="Employment History" stepId="jobEntries">
        {state.jobEntries.map((job, i) => (
          <div key={i} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: i < state.jobEntries.length - 1 ? '1px solid var(--border)' : 'none' }}>
            {job.is_school ? (
              <>
                <div style={{ fontWeight: 700 }}>School</div>
                <div style={{ fontSize: 14, color: 'var(--text-light)' }}>
                  In school prior to the entry above — history complete
                </div>
              </>
            ) : job.is_non_working ? (
              <>
                <div style={{ fontWeight: 700 }}>Non-working period{job.reason ? ` — ${job.reason}` : ''}</div>
                <div style={{ fontSize: 14, color: 'var(--text-light)' }}>
                  {formatMonthYear(job.start_date)} – {job.end_date ? formatMonthYear(job.end_date) : 'Present'}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 700 }}>{job.employer_name}</div>
                <div style={{ fontSize: 14, color: 'var(--text-light)' }}>
                  {job.city}, {job.state} | {formatMonthYear(job.start_date)} – {job.end_date ? formatMonthYear(job.end_date) : 'Present'}
                </div>
                <div style={{ fontSize: 14 }}>{job.position} | {job.dot_regulated ? 'DOT' : 'Non-DOT'}</div>
              </>
            )}
          </div>
        ))}
      </Section>

      <Section title="On-Duty Hours (Last 7 Days)" stepId="dotHours">
        <Row label="Logged DOT hours" value={state.dotHours.answer ? state.dotHours.answer.toUpperCase() : null} />
        {(state.dotHours.days || []).map(d => (
          <div key={d.date} className="review-row">
            <span className="review-label">{formatDayLabel(d.date)}: </span>{d.hours}
          </div>
        ))}
        <div className="review-row" style={{ fontWeight: 700, marginTop: 6 }}>
          <span className="review-label">Total: </span>{totalHours(state.dotHours.days)}
        </div>
      </Section>

      <Section title="Signature" stepId="signature">
        <Row label="Printed Name" value={sig.printedName} />
        <Row label="Electronically Signed" value={sig.signatureData ? 'Yes ✅' : 'No ⚠️'} />
      </Section>

      <div className="step-card" style={{ marginTop: 8 }}>
        <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 16, textAlign: 'center' }}>
          By submitting this form, you confirm that all information provided is true and correct to the best of your knowledge.
        </p>
        <button className="btn-submit" onClick={onNext}>Submit Application</button>
      </div>
    </div>
  );
}
